import React, { ReactNode, useEffect, useCallback } from 'react';
import { useMobileChart } from '../../context/MobileChartContext';
import { useResponsiveCharts } from '../../hooks/useResponsiveCharts';

interface ResponsiveChartWrapperProps {
  children: ReactNode;
  title: string;
  className?: string;
  setChartRef?: (ref: any) => void;
}

/**
 * A wrapper component that makes charts responsive across different devices
 * 
 * Features:
 * - On mobile: Shows a "Tap to expand" indicator and opens charts in fullscreen overlay
 * - On desktop: Preserves existing behavior while ensuring optimal display
 * - Sets up proper refs and handlers for the chart component
 */
const ResponsiveChartWrapper: React.FC<ResponsiveChartWrapperProps> = ({
  children,
  title,
  className = '',
  setChartRef
}) => {
  const { isMobile } = useMobileChart();
  const chartRef = useResponsiveCharts(title);
  
  // Pass the ref to parent component if needed
  useEffect(() => {
    if (setChartRef) {
      setChartRef(chartRef);
    }
  }, [setChartRef, chartRef]);
  
  // Handle chart click on mobile
  const handleChartClick = useCallback(() => {
    if (isMobile) {
      // Get the chart component from children
      chartRef.current.getChartComponent = () => children;
      chartRef.current.openModal();
    }
  }, [isMobile, children, chartRef]);
  
  // On mobile, add touch-friendly features
  if (isMobile) {
    return (
      <div 
        className={`relative cursor-pointer ${className}`}
        onClick={handleChartClick}
      >
        {/* Invisible overlay to capture taps */}
        <div className="absolute inset-0 z-10 bg-transparent" />
        
        {/* Original chart */}
        {children}
        
        {/* Mobile interaction hint */}
        <div className="absolute bottom-2 right-2 bg-white/80 backdrop-blur-sm text-slate-700 text-xs py-1 px-2 rounded-md shadow-sm">
          Tap to expand
        </div>
      </div>
    );
  }
  
  // On desktop, just render the chart with the reference
  return (
    <div className={className} ref={(node) => {
      if (node && chartRef.current) {
        if (!chartRef.current.node) {
          chartRef.current.node = node;
        }
      }
    }}>
      {children}
    </div>
  );
};

export default ResponsiveChartWrapper; 